import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import {
  ComputeEvaluationScores,
  GetCheckpointScore,
  GetObtainedByCheck,
} from '../serBasicScore';

/** 기본 대분류 라벨 */
export const SER_EVAL_DEFAULT_STEP_LABELS = [
  '경영자 안전보건경영활동',
  '위험성 평가',
  '안전보건교육',
  '근로자 참여',
  '중대재해 대응조치',
  '도급.용역.위탁 안전보건확보',
  '평가 및 개선',
  '안전문화 및 안전의식',
  '가점 사항',
];

/** 엑셀 시트 구성 방식 */
export const EXCEL_SHEET_MODE = {
  SPLIT: 'split',
  SINGLE: 'single',
};

const EXCEL_FONT = '맑은 고딕';
const EXCEL_BORDER = {
  top: { style: 'thin', color: { argb: 'FF808080' } },
  left: { style: 'thin', color: { argb: 'FF808080' } },
  bottom: { style: 'thin', color: { argb: 'FF808080' } },
  right: { style: 'thin', color: { argb: 'FF808080' } },
};

const ApplyHeaderRowStyle = (headerRow, columnCount) => {
  headerRow.font = { name: EXCEL_FONT, size: 11, bold: true };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
  for (let c = 1; c <= columnCount; c += 1) {
    const cell = headerRow.getCell(c);
    cell.border = EXCEL_BORDER;
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF3F7F8' },
    };
  }
};

const ApplyDataRowStyle = (row, columnCount, textColumnIndex = 3) => {
  for (let c = 1; c <= columnCount; c += 1) {
    const cell = row.getCell(c);
    cell.font = { name: EXCEL_FONT, size: 10 };
    cell.alignment = {
      vertical: 'middle',
      horizontal: c === textColumnIndex ? 'left' : 'center',
      wrapText: c === textColumnIndex,
    };
    cell.border = EXCEL_BORDER;
  }
};

const GetEvaDateOnly = (dateValue = {}) => {
  const evaDateRaw = dateValue.evaDate;
  if (evaDateRaw == null || String(evaDateRaw).trim() === '') return '';
  if (dayjs(evaDateRaw).isValid()) {
    return dayjs(evaDateRaw).format('YYYY-MM-DD');
  }
  return String(evaDateRaw).split(/[T ]/)[0].slice(0, 10);
};

const WriteWorksheetMetaRows = (ws, { dateValue = {}, totalPoint = 0, custNm = '' } = {}) => {
  ws.getRow(1).values = [
    '평가일자',
    GetEvaDateOnly(dateValue),
    '평가자',
    dateValue.evaName || '',
    '총 취득 점수',
    Math.round(Number(totalPoint) || 0),
  ];
  ws.getRow(2).values = ['사업장명', custNm || '-', '', '', '', ''];
};

const WriteIndicatorRows = (
  ws,
  startRow,
  {
    stepRows = [],
    arrayList2 = [],
    categoryLabel = '',
    includeCategory = false,
  } = {}
) => {
  let excelRow = startRow;
  const columnCount = includeCategory ? 7 : 6;
  const textColumnIndex = includeCategory ? 4 : 3;

  stepRows.forEach((_item, _index) => {
    const children = arrayList2.filter((e) => e.groupCd === _item.code);
    const subtotalLabel = `${_item.val1}-${_index + 1}`;
    const scoreDisplay = _item.val3 === '' || _item.val3 == null ? 0 : _item.val3;

    const writeDataRow = (child) => {
      const row = ws.getRow(excelRow);
      const checkpointScore = child
        ? GetCheckpointScore(child, _item, children)
        : '';
      const obtained = child
        ? GetObtainedByCheck(child.check, checkpointScore)
        : scoreDisplay;

      if (includeCategory) {
        row.getCell(1).value = categoryLabel;
        row.getCell(2).value = subtotalLabel;
        row.getCell(3).value = _item.codeNm || '';
        row.getCell(4).value = child?.codeNm || '';
        row.getCell(5).value = checkpointScore === '' ? '' : checkpointScore;
        row.getCell(6).value = FormatSerEvalCheck(child?.check);
        row.getCell(7).value = obtained;
      } else {
        row.getCell(1).value = subtotalLabel;
        row.getCell(2).value = _item.codeNm || '';
        row.getCell(3).value = child?.codeNm || '';
        row.getCell(4).value = checkpointScore === '' ? '' : checkpointScore;
        row.getCell(5).value = FormatSerEvalCheck(child?.check);
        row.getCell(6).value = obtained;
      }

      ApplyDataRowStyle(row, columnCount, textColumnIndex);
      excelRow += 1;
    };

    if (!children.length) {
      writeDataRow(null);
    } else {
      children.forEach((child) => writeDataRow(child));
    }
  });

  return excelRow;
};

/** 평가 라디오 값 → 표시문구 */
export const FormatSerEvalCheck = (check) => {
  if (check === 'Y') return '양호';
  if (check === 'N') return '미흡';
  return '해당없음';
};

/** Excel 시트명 금지문자 제거·길이 제한(31자) */
export const SanitizeExcelSheetName = (name, maxLen = 31) => {
  const cleaned = String(name ?? '')
    .replace(/[:\\/?*[\]]/g, '_')
    .trim();
  const base = cleaned || 'Sheet';
  return base.length > maxLen ? base.slice(0, maxLen) : base;
};

export const CreateDefaultSteps = (count = SER_EVAL_DEFAULT_STEP_LABELS.length) =>
  SER_EVAL_DEFAULT_STEP_LABELS.slice(0, count).map((label, i) => ({
    id: `cat-${i + 1}`,
    label,
  }));

const RecomputeIndicatorScores = (arrayList, arrayList2) => {
  const { updatedList, total } = ComputeEvaluationScores(arrayList, arrayList2);
  return {
    arrayList: updatedList,
    totalPoint: total,
  };
};

/** 상세 API 응답 → 평가 구조 (레거시/양식 미지정) */
export const MapDetailApiRowsToEvaluation = (data = []) => {
  if (!Array.isArray(data) || !data.length) return null;

  const groupedData = data.reduce((acc, item) => {
    const key = item.codeNm;
    if (!acc[key]) {
      acc[key] = {
        codeNm: key,
        val1: item.val1,
        val2: item.val2,
        code: item.groupCd,
        val3: 0,
      };
    }
    return acc;
  }, {});

  const arrayList = Object.values(groupedData);
  const arrayList2 = [];

  arrayList.forEach((indicator) => {
    const children = data.filter((e) => e.groupCd === indicator.code);
    let totalPo = 0;

    children.forEach((item) => {
      const check = item.checkYn || 'N';
      const cpScore = GetCheckpointScore(
        { val2: null },
        indicator,
        children
      );
      if (check === 'Y') totalPo += cpScore;
      arrayList2.push({
        code: item.seriousDisaster,
        groupCd: item.groupCd,
        codeNm: item.seriousDisasterNm,
        check,
        val1: item.val1,
      });
    });
    indicator.val3 = totalPo.toFixed(2);
  });

  const maxStep = Math.max(
    ...arrayList.map((row) => Number(row.val1) || 1),
    1
  );
  const steps = CreateDefaultSteps(maxStep);
  const totalPoint = arrayList.reduce(
    (sum, row) => sum + Number(row.val3 || 0),
    0
  );

  return {
    steps,
    arrayList,
    arrayList2,
    totalPoint: Number(totalPoint.toFixed(2)),
  };
};

/** 양식 구조 + 상세 평가값 병합 */
export const MergeTemplateWithDetailRows = (formData, detailRows = []) => {
  const steps =
    formData?.steps?.length > 0
      ? formData.steps
      : CreateDefaultSteps();
  const baseList = formData?.arrayList || [];
  const checkMap = new Map(
    detailRows.map((r) => [r.seriousDisaster, r.checkYn || '0'])
  );
  const arrayList2 = (formData?.arrayList2 || []).map((row) => ({
    ...row,
    check: checkMap.has(row.code) ? checkMap.get(row.code) : row.check || 'N',
  }));
  const { arrayList, totalPoint } = RecomputeIndicatorScores(baseList, arrayList2);

  return { steps, arrayList, arrayList2, totalPoint };
};

/**
 * 중대재해 평가 한 단계(val1)를 워크시트에 기록
 * @param {object} ctx - { arrayList, arrayList2, dateValue, totalPoint, custNm }
 */
export const WriteSeriousAccidentStepToWorksheet = (ws, stepVal, ctx) => {
  const { arrayList = [], arrayList2 = [], dateValue = {}, totalPoint = 0, custNm = '' } =
    ctx;
  const stepValStr = String(stepVal);
  ws.views = [{ state: 'frozen', ySplit: 3 }];

  WriteWorksheetMetaRows(ws, { dateValue, totalPoint, custNm });

  const headerRow = ws.getRow(3);
  headerRow.values = ['소계', '평가지표', '착안사항', '배점', '평가', '취득점수'];
  ApplyHeaderRowStyle(headerRow, 6);

  const stepRows = arrayList.filter((e) => String(e.val1) === stepValStr);
  const nextRow = WriteIndicatorRows(ws, 4, { stepRows, arrayList2 });

  if (!stepRows.length) {
    const emptyRow = ws.getRow(nextRow);
    emptyRow.getCell(1).value = '(해당 단계 평가 항목 없음)';
    emptyRow.getCell(1).font = { name: EXCEL_FONT, size: 10, italic: true };
  }

  ws.columns = [
    { width: 10 },
    { width: 28 },
    { width: 48 },
    { width: 10 },
    { width: 12 },
    { width: 12 },
  ];
};

/** 전체 대분류를 단일 워크시트에 기록 */
export const WriteSeriousAccidentAllToWorksheet = (ws, ctx) => {
  const {
    steps = [],
    arrayList = [],
    arrayList2 = [],
    dateValue = {},
    totalPoint = 0,
    custNm = '',
  } = ctx;

  ws.views = [{ state: 'frozen', ySplit: 3 }];
  WriteWorksheetMetaRows(ws, { dateValue, totalPoint, custNm });

  const headerRow = ws.getRow(3);
  headerRow.values = [
    '대분류',
    '소계',
    '평가지표',
    '착안사항',
    '배점',
    '평가',
    '취득점수',
  ];
  ApplyHeaderRowStyle(headerRow, 7);

  let excelRow = 4;
  steps.forEach((step, idx) => {
    const stepNo = String(idx + 1);
    const stepRows = arrayList.filter((e) => String(e.val1) === stepNo);
    const categoryLabel = `${idx + 1}. ${step.label || ''}`;

    if (!stepRows.length) {
      const emptyRow = ws.getRow(excelRow);
      emptyRow.getCell(1).value = categoryLabel;
      emptyRow.getCell(2).value = '(평가 항목 없음)';
      ApplyDataRowStyle(emptyRow, 7, 4);
      excelRow += 1;
      return;
    }

    excelRow = WriteIndicatorRows(ws, excelRow, {
      stepRows,
      arrayList2,
      categoryLabel,
      includeCategory: true,
    });
  });

  ws.columns = [
    { width: 22 },
    { width: 10 },
    { width: 28 },
    { width: 48 },
    { width: 10 },
    { width: 12 },
    { width: 12 },
  ];
};

/** 전체 단계 워크북 생성 */
export const BuildSeriousAccidentWorkbook = (
  ctx,
  { sheetMode = EXCEL_SHEET_MODE.SPLIT } = {}
) => {
  const { steps = [], arrayList = [] } = ctx;
  if (!arrayList.length) return null;

  const workbook = new ExcelJS.Workbook();

  if (sheetMode === EXCEL_SHEET_MODE.SINGLE) {
    const ws = workbook.addWorksheet(
      SanitizeExcelSheetName('중대재해처벌법_평가')
    );
    WriteSeriousAccidentAllToWorksheet(ws, ctx);
    return workbook;
  }

  steps.forEach((step, idx) => {
    const stepNo = idx + 1;
    const sheetName = SanitizeExcelSheetName(`${stepNo}_${step.label}`);
    const ws = workbook.addWorksheet(sheetName);
    WriteSeriousAccidentStepToWorksheet(ws, String(stepNo), ctx);
  });

  return workbook;
};

/** 워크북 파일 다운로드 */
export const DownloadSeriousAccidentWorkbook = async (workbook, fileName) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

/** 일괄 엑셀 파일명 */
export const BuildSeriousAccidentExcelFileName = ({
  evaDate,
  evaName,
  suffix,
  sheetMode = EXCEL_SHEET_MODE.SPLIT,
} = {}) => {
  const datePart = evaDate
    ? dayjs(evaDate).isValid()
      ? dayjs(evaDate).format('YYYYMMDD')
      : String(evaDate).slice(0, 10).replace(/-/g, '')
    : dayjs().format('YYYYMMDD');
  const namePart = evaName ? `_${String(evaName).slice(0, 10)}` : '';
  const tail = suffix || dayjs().format('HHmmss');
  const modePart =
    sheetMode === EXCEL_SHEET_MODE.SINGLE ? '_단일시트' : '_대분류별시트';
  return `중대재해처벌법_평가${modePart}_${datePart}${namePart}_${tail}.xlsx`;
};

/** 평가 컨텍스트 기준 일괄 엑셀 다운로드 */
export const DownloadSeriousAccidentExcelAll = async (
  ctx,
  fileName,
  { sheetMode = EXCEL_SHEET_MODE.SPLIT } = {}
) => {
  const workbook = BuildSeriousAccidentWorkbook(ctx, { sheetMode });
  if (!workbook) return false;
  await DownloadSeriousAccidentWorkbook(
    workbook,
    fileName ||
      BuildSeriousAccidentExcelFileName({
        evaDate: ctx?.dateValue?.evaDate,
        evaName: ctx?.dateValue?.evaName,
        sheetMode,
      })
  );
  return true;
};
